import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {action, makeAutoObservable, observable, runInAction} from "mobx";
import type {ILeaguesStore} from "data/stores/leagues/leagues.store";
import {ILeague} from "data/providers/api/league.api.provider";
import {uniqBy} from "lodash";
import {RequestState, ShareType, SocialNetwork} from "data/enums";
import {trackSentryErrors} from "data/utils";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ShareUtility} from "data/utils/social_share/social_share.utility";
import type {ITitleStore} from "data/stores/title/title.store";
import {EMPTY_LEAGUE_DATA} from "views/pages/leagues/join_leagues/join_leagues.page";
import type {INotificationService} from "data/services/Notification.service";
import React from "react";
import {EMAIL_REGEXP} from "data/constants";
import {AxiosError} from "axios";
import {IAxiosApiError} from "data/types/global";
import type {IUserStore} from "data/stores/user/user.store";

export interface IInviteLeagueController extends ViewController {
	showInviteNotification: () => void;
	sendEmailsRequest: () => void;
	shareTw: () => void;
	shareFB: () => void;
	shareMobile: () => void;
	handleEmailChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;

	get isEmailInProcess(): boolean;

	get isEmailProcessed(): boolean;

	get isEmailsSendDisabled(): boolean;

	get emailString(): string;

	get areEmailsValid(): boolean;

	get league(): ILeague;

	get isCommisioner(): boolean;
}

@injectable()
export class InviteLeagueController implements IInviteLeagueController {
	@observable private _inviteEmailsState: RequestState = RequestState.IDLE;

	constructor(
		@inject(Bindings.LeaguesStore) private _leaguesStore: ILeaguesStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.TitleStore) private _titleStore: ITitleStore,
		@inject(Bindings.NotificationService) private _notificationService: INotificationService,
		@inject(Bindings.UserStore) private _userStore: IUserStore
	) {
		makeAutoObservable(this);
	}

	@observable private _emailString: string = "";

	get isCommisioner(): boolean {
		return this.league.userId === this._userStore.user?.id;
	}
	get emailString(): string {
		return this._emailString;
	}

	@observable private _areEmailsValid: boolean = true;

	get areEmailsValid() {
		return this._areEmailsValid;
	}

	get league() {
		return this._leaguesStore.selectedLeague || EMPTY_LEAGUE_DATA;
	}

	get isEmailInProcess(): boolean {
		return this._inviteEmailsState === RequestState.PENDING;
	}

	get isEmailProcessed(): boolean {
		return this._inviteEmailsState === RequestState.SUCCESS;
	}

	get isEmailsSendDisabled(): boolean {
		return !this.emailString || this.isEmailInProcess || this.isEmailProcessed;
	}

	get _shareMessage() {
		return `Join this league and be a part of the fun! Elite team name is essential!`;
	}

	@action
	handleEmailChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		this._emailString = event.currentTarget.value;
		if (!this.areEmailsValid) {
			this.setEmailsValid(true);
		}
	};

	@action
	sendEmailsRequest = () => {
		if (!this.league) return;

		this.setEmailsValid(true);
		const emailsArray = this.parseEmails();
		const hasInvalidEmails = emailsArray.some(
			(it) => it.length && !new RegExp(EMAIL_REGEXP).test(it)
		);

		if (hasInvalidEmails || emailsArray.length === 0) {
			this.setEmailsValid(false);
			return;
		}

		const identityEmails = uniqBy(
			emailsArray.filter(Boolean).map((email) => ({
				email,
			})),
			"email"
		);

		this.setEmailsRequestState(RequestState.PENDING);
		this._leaguesStore
			.inviteLeague({
				league_id: this.league.id,
				emails: identityEmails,
			})
			.then((_e) => {
				this.setEmailsRequestState(RequestState.SUCCESS);
				this.showInviteNotification();
			})
			.catch((e) => {
				trackSentryErrors(e, {}, "league invite");
				const error = e as AxiosError<IAxiosApiError, unknown>;
				this._modalsStore.showAxiosError(error);
				this.setEmailsRequestState(RequestState.ERROR);
			})
			.finally(() => {
				setTimeout(() => {
					this.setEmailsRequestState(RequestState.IDLE);
				}, 2000);
			});
	};

	shareTw = () => {
		if (!this.league) {
			return;
		}
		ShareUtility.share({
			message: this._shareMessage,
			type: ShareType.LEAGUE,
			leagueId: this.league.id,
			socialNetwork: SocialNetwork.TWITTER,
		});
	};

	shareFB = () => {
		if (!this.league) {
			return;
		}

		ShareUtility.share({
			message: this._shareMessage,
			type: ShareType.LEAGUE,
			leagueId: this.league.id,
			socialNetwork: SocialNetwork.FACEBOOK,
		});
	};

	shareMobile = () => {
		const league = this.league;
		if (league) {
			ShareUtility.share({
				message: this._shareMessage,
				type: ShareType.LEAGUE,
				leagueId: league.id,
				socialNetwork: SocialNetwork.MOBILE,
			});
		}
	};

	public showInviteNotification = () => {
		this._notificationService.showNotification({
			text: "Invites Sent Successfully!",
			cancellable: true,
		});
	};

	dispose(): void {
		return;
	}

	init(): void {
		this.setTitle();
	}

	setTitle() {
		if (!this._leaguesStore.selectedLeague) {
			return;
		}
		void this._titleStore.setTitle(this._leaguesStore.selectedLeague?.name);
	}

	@action
	private setEmailsRequestState(state: RequestState) {
		runInAction(() => {
			this._inviteEmailsState = state;
		});
	}

	private setEmailsValid(isValid: boolean) {
		runInAction(() => {
			this._areEmailsValid = isValid;
		});
	}

	private parseEmails(): string[] {
		const emailsByNewLine = this.emailString.replace(/\n/g, ",").replace(/,+/, ",");
		return emailsByNewLine.split(",").map((it) => it.trim());
	}
}
